import React, { useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import IconButton from './IconButton';
import { cx } from './toneStyles';
import { useKeyboardInset } from '../hooks/useKeyboardInset';

/**
 * BottomSheet — phone-first dialog anchored to the bottom (centred like Modal from md:).
 *
 * Props
 *   isOpen / onClose
 *   title        string → header row with a close IconButton (aria-labelledby)
 *   children     scrollable body
 *   footer       node in a sticky footer (primary action) with safe-area-bottom padding
 *   dismissible  scrim tap + Escape close it (default true); false hides the close button
 *   size         'md' (max-w-md at md:, default) | 'lg' (max-w-lg)
 *
 * Own focus trap + Escape + body scroll lock (same logic as Modal); role="dialog"
 * aria-modal="true"; rounded-t-3xl + grab handle + animate-slideUp on phones; scrim
 * bg-black/50 dark:bg-black/70.
 *
 * Sheets stack: a sheet may open another (WakeUpSheet → TimePickerSheet). Every sheet
 * portals to document.body, so z-order is DOM order, and the body scroll lock is counted
 * at module level — it is released only when the LAST open sheet closes, so closing an
 * inner sheet never un-locks the page under an outer one that is still open. Escape is
 * stopped at the innermost sheet (stopPropagation), so it never closes the outer one, and
 * focus returns to whatever was active when the sheet opened.
 *
 * Keyboard: on iOS the Keyboard plugin (resize "body") only shrinks <body>, which this
 * fixed layer ignores, so the keyboard used to slide over the sheet's footer and the end
 * of its body. While useKeyboardInset() reports a keyboard, the layer gets that much
 * bottom padding (the scrim still covers the whole screen), the panel may use the strip
 * above it up to the status bar, the home-indicator padding is dropped (the keyboard
 * covers that edge), and a focused field in the body is scrolled back into view.
 */
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const SIZES = { md: 'md:max-w-md', lg: 'md:max-w-lg' };
// Panel height cap while the keyboard is up: the usual 90 % of the space above it, but
// never under the status bar / Dynamic Island.
const KEYBOARD_PANEL_MAX_HEIGHT = 'min(90%, calc(100% - env(safe-area-inset-top, 0px) - 8px))';

// Number of sheets currently holding the body scroll lock (module-wide, never negative).
let openSheetCount = 0;

function acquireBodyLock() {
  openSheetCount += 1;
  document.body.style.overflow = 'hidden';
}

function releaseBodyLock() {
  openSheetCount = Math.max(0, openSheetCount - 1);
  if (openSheetCount === 0) document.body.style.overflow = '';
}

export default function BottomSheet({ isOpen, onClose, title, children, footer, dismissible = true, size = 'md', className = '' }) {
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const previousFocusRef = useRef(null);
  // Whether THIS instance currently holds one count of the body lock — the close branch
  // and the effect cleanup both release, and only the first of them may decrement.
  const holdsLockRef = useRef(false);
  const titleId = useId();
  const keyboardInset = useKeyboardInset();
  const keyboardOpen = keyboardInset > 0;

  const requestClose = useCallback(() => {
    if (dismissible && onClose) onClose();
  }, [dismissible, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [requestClose]
  );

  useEffect(() => {
    const release = () => {
      if (!holdsLockRef.current) return;
      holdsLockRef.current = false;
      releaseBodyLock();
    };
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      if (!holdsLockRef.current) {
        holdsLockRef.current = true;
        acquireBodyLock();
      }
      requestAnimationFrame(() => {
        if (dialogRef.current) {
          const firstFocusable = dialogRef.current.querySelector(FOCUSABLE);
          if (firstFocusable) firstFocusable.focus();
        }
      });
    } else {
      release();
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }
    return release;
  }, [isOpen]);

  // Lifting the sheet shrinks its body from below: bring a focused field back into view.
  useEffect(() => {
    if (!isOpen || !keyboardOpen) return;
    const el = document.activeElement;
    if (el && bodyRef.current?.contains(el) && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, keyboardOpen, keyboardInset]);

  if (!isOpen) return null;

  const node = (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-4"
      style={keyboardOpen ? { paddingBottom: keyboardInset } : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70 animate-fadeIn"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className={cx(
          'relative w-full flex flex-col bg-white dark:bg-night-surface shadow-xl dark:ring-1 dark:ring-white/5',
          'rounded-t-3xl max-h-[90vh] animate-slideUp',
          'md:rounded-2xl md:max-h-[85vh] md:animate-fadeIn',
          SIZES[size] || SIZES.md,
          className
        )}
        style={keyboardOpen ? { maxHeight: KEYBOARD_PANEL_MAX_HEIGHT } : undefined}
      >
        <div className="md:hidden flex justify-center pt-2 pb-1">
          <span className="w-10 h-1.5 rounded-full bg-surface-300 dark:bg-night-border" aria-hidden="true" />
        </div>
        {(title || dismissible) && (
          <div className="flex items-center justify-between gap-2 pl-4 pr-2 py-1 md:py-2 md:border-b md:border-surface-200 md:dark:border-night-border">
            {title ? (
              <h2 id={titleId} className="text-base font-semibold text-ink dark:text-night-text truncate">
                {title}
              </h2>
            ) : (
              <span className="flex-1" aria-hidden="true" />
            )}
            {dismissible && <IconButton icon={MdClose} label="Close" onClick={onClose} />}
          </div>
        )}
        <div
          ref={bodyRef}
          className={cx('flex-1 overflow-y-auto overscroll-contain px-4 pt-2 pb-4', !footer && !keyboardOpen && 'safe-area-bottom')}
        >
          {children}
        </div>
        {footer && (
          <div
            className={cx(
              'flex-shrink-0 px-4 pt-3 pb-3 border-t border-surface-200 dark:border-night-border bg-white dark:bg-night-surface md:rounded-b-2xl',
              !keyboardOpen && 'safe-area-bottom'
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}
